import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../../utils/apiClient';
import {
  Style,
  InventoryItem,
  InventorySearchParams,
  LocationSuggestion,
  LocationRequest,
  StorageBin,
  LocationData,
  InventoryUploadResult,
  InventoryStatusReport,
  StyleFilter,
  InventoryFilter,
  InventoryAdjustmentRequest,  InventoryAdjustmentRecord,
  InventoryAdjustmentSummary,
  AvailableItem
} from '../../types/inventory';

interface InventoryState {
  // Styles
  styles: Style[];
  stylesLoading: boolean;
  stylesError: string | null;
  stylesPagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    per_page: number;
  };
  styleFilters: StyleFilter;
  selectedStyle: Style | null;

  // Inventory items
  inventoryItems: InventoryItem[];
  inventoryLoading: boolean;
  inventoryError: string | null;
  inventoryFilters: InventoryFilter;
  // Search results
  searchResults: InventoryItem[];
  searchLoading: boolean;
  searchError: string | null;

  // Available items
  availableItems: AvailableItem[];
  availableItemsLoading: boolean;
  availableItemsError: string | null;
  availableItemsPagination: {
    current_page: number;
    total_pages: number;
    total_records: number;
    per_page: number;
  };

  // Location data
  bins: StorageBin[];
  racks: LocationData | null;
  locationsLoading: boolean;
  locationsError: string | null;

  // Location suggestions
  locationSuggestions: LocationSuggestion[];
  suggestionsLoading: boolean;
  suggestionsError: string | null;

  // File uploads
  uploadLoading: boolean;
  uploadError: string | null;
  uploadResult: InventoryUploadResult | null;

  // Reports
  inventoryStatus: InventoryStatusReport | null;
  reportsLoading: boolean;
  reportsError: string | null;

  // Inventory adjustments
  adjustmentLoading: boolean;
  adjustmentError: string | null;
  adjustmentSuccess: boolean;
  recentAdjustments: InventoryAdjustmentRecord[];
  adjustmentSummary: InventoryAdjustmentSummary | null;
  adjustmentsLoading: boolean;
}

const initialState: InventoryState = {
  styles: [],
  stylesLoading: false,
  stylesError: null,
  stylesPagination: {
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 50
  },
  styleFilters: {
    page: 1,
    limit: 50
  },
  selectedStyle: null,

  inventoryItems: [],
  inventoryLoading: false,
  inventoryError: null,
  inventoryFilters: {
    page: 1,
    limit: 50
  },
  searchResults: [],
  searchLoading: false,
  searchError: null,

  availableItems: [],
  availableItemsLoading: false,
  availableItemsError: null,
  availableItemsPagination: {
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 50
  },

  bins: [],
  racks: null,
  locationsLoading: false,
  locationsError: null,

  locationSuggestions: [],
  suggestionsLoading: false,
  suggestionsError: null,

  uploadLoading: false,
  uploadError: null,
  uploadResult: null,

  inventoryStatus: null,
  reportsLoading: false,
  reportsError: null,

  adjustmentLoading: false,
  adjustmentError: null,
  adjustmentSuccess: false,
  recentAdjustments: [],
  adjustmentSummary: null,
  adjustmentsLoading: false
};

// Async thunks for API calls
export const fetchStyles = createAsyncThunk(
  'inventory/fetchStyles',
  async (filters: StyleFilter, { rejectWithValue }) => {
    try {
      const response = await apiClient.getStyles(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch styles');
    }
  }
);

export const createStyle = createAsyncThunk(
  'inventory/createStyle',
  async (styleData: Partial<Style>, { rejectWithValue }) => {
    try {
      const response = await apiClient.createStyle(styleData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create style');
    }
  }
);

export const searchInventory = createAsyncThunk(
  'inventory/searchInventory',
  async (params: InventorySearchParams, { rejectWithValue }) => {
    try {
      const response = await apiClient.searchInventory(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to search inventory');
    }
  }
);

export const suggestLocation = createAsyncThunk(
  'inventory/suggestLocation',
  async (request: LocationRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.suggestLocation(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to get location suggestions');
    }
  }
);

export const fetchBins = createAsyncThunk(
  'inventory/fetchBins',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.getBins();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch bins');
    }
  }
);

export const fetchRacks = createAsyncThunk(
  'inventory/fetchRacks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.getRacks();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch racks');
    }
  }
);

export const uploadShopifyInventory = createAsyncThunk(
  'inventory/uploadShopifyInventory',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await apiClient.uploadShopifyInventory(file);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload inventory file');
    }
  }
);

export const uploadShopifyStock = createAsyncThunk(
  'inventory/uploadShopifyStock',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await apiClient.uploadShopifyStock(file);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload stock file');
    }
  }
);

export const uploadManualStock = createAsyncThunk(
  'inventory/uploadManualStock',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await apiClient.uploadManualStock(file);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload manual stock file');
    }
  }
);

export const uploadBinAllocation = createAsyncThunk(
  'inventory/uploadBinAllocation',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await apiClient.uploadBinAllocation(file);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload bin allocation file');
    }
  }
);

export const fetchInventoryStatus = createAsyncThunk(
  'inventory/fetchInventoryStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.getInventoryStatus();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch inventory status');
    }
  }
);

// Inventory adjustment actions
export const submitInventoryAdjustment = createAsyncThunk(
  'inventory/submitInventoryAdjustment',
  async (adjustmentData: InventoryAdjustmentRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.adjustInventory(adjustmentData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to process inventory adjustment');
    }
  }
);

export const fetchRecentAdjustments = createAsyncThunk(
  'inventory/fetchRecentAdjustments',
  async (params: { limit?: number; days?: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.getRecentAdjustments(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch recent adjustments');
    }
  }
);

export const fetchAdjustmentSummary = createAsyncThunk(
  'inventory/fetchAdjustmentSummary',
  async (params: { days?: number }, { rejectWithValue }) => {
    try {
      const response = await apiClient.getAdjustmentSummary(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch adjustment summary');
    }
  }
);

export const fetchAvailableItems = createAsyncThunk(
  'inventory/fetchAvailableItems',
  async (params: { search?: string; color?: string; size?: string; page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await apiClient.getAvailableItems(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch available items');
    }
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setStyleFilters: (state, action: PayloadAction<StyleFilter>) => {
      state.styleFilters = action.payload;
    },
    setInventoryFilters: (state, action: PayloadAction<InventoryFilter>) => {
      state.inventoryFilters = action.payload;
    },
    setSelectedStyle: (state, action: PayloadAction<Style | null>) => {
      state.selectedStyle = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
    clearLocationSuggestions: (state) => {
      state.locationSuggestions = [];
      state.suggestionsError = null;
    },
    clearUploadResult: (state) => {
      state.uploadResult = null;
      state.uploadError = null;
    },
    clearError: (state) => {
      state.searchError = null;
      state.locationsError = null;
      state.suggestionsError = null;
      state.uploadError = null;
      state.reportsError = null;
      state.adjustmentError = null;
    },
    clearAdjustmentMessages: (state) => {
      state.adjustmentError = null;
      state.adjustmentSuccess = false;
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // Fetch styles
      .addCase(fetchStyles.pending, (state) => {
        state.stylesLoading = true;
        state.stylesError = null;
      })
      .addCase(fetchStyles.fulfilled, (state, action) => {
        state.stylesLoading = false;
        state.styles = action.payload.data.styles;
        state.stylesPagination = action.payload.data.pagination;
      })
      .addCase(fetchStyles.rejected, (state, action) => {
        state.stylesLoading = false;
        state.stylesError = action.payload as string;
      })

      // Create style
      .addCase(createStyle.pending, (state) => {
        state.stylesLoading = true;
        state.stylesError = null;
      })
      .addCase(createStyle.fulfilled, (state) => {
        state.stylesLoading = false;
        // Refresh styles list after creation
      })
      .addCase(createStyle.rejected, (state, action) => {
        state.stylesLoading = false;
        state.stylesError = action.payload as string;
      })

      // Search inventory
      .addCase(searchInventory.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchInventory.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.data;
      })
      .addCase(searchInventory.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload as string;
      })

      // Fetch available items
      .addCase(fetchAvailableItems.pending, (state) => {
        state.availableItemsLoading = true;
        state.availableItemsError = null;
      })
      .addCase(fetchAvailableItems.fulfilled, (state, action) => {
        state.availableItemsLoading = false;
        state.availableItems = action.payload.data.available_items;
        state.availableItemsPagination = action.payload.data.pagination;
      })
      .addCase(fetchAvailableItems.rejected, (state, action) => {
        state.availableItemsLoading = false;
        state.availableItemsError = action.payload as string;
      })

      // Suggest location
      .addCase(suggestLocation.pending, (state) => {
        state.suggestionsLoading = true;
        state.suggestionsError = null;
      })
      .addCase(suggestLocation.fulfilled, (state, action) => {
        state.suggestionsLoading = false;
        state.locationSuggestions = action.payload.suggested_locations;
      })
      .addCase(suggestLocation.rejected, (state, action) => {
        state.suggestionsLoading = false;
        state.suggestionsError = action.payload as string;
      })

      // Fetch bins
      .addCase(fetchBins.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(fetchBins.fulfilled, (state, action) => {
        state.locationsLoading = false;
        state.bins = action.payload.data as StorageBin[];
      })
      .addCase(fetchBins.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload as string;
      })

      // Fetch racks
      .addCase(fetchRacks.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(fetchRacks.fulfilled, (state, action) => {
        state.locationsLoading = false;
        state.racks = action.payload.data as LocationData;
      })
      .addCase(fetchRacks.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload as string;
      })

      // Upload Shopify inventory
      .addCase(uploadShopifyInventory.pending, (state) => {
        state.uploadLoading = true;
        state.uploadError = null;
        state.uploadResult = null;
      })
      .addCase(uploadShopifyInventory.fulfilled, (state, action) => {
        state.uploadLoading = false;
        state.uploadResult = action.payload;
      })
      .addCase(uploadShopifyInventory.rejected, (state, action) => {
        state.uploadLoading = false;
        state.uploadError = action.payload as string;
      })

      // Upload Shopify stock
      .addCase(uploadShopifyStock.pending, (state) => {
        state.uploadLoading = true;
        state.uploadError = null;
        state.uploadResult = null;
      })
      .addCase(uploadShopifyStock.fulfilled, (state, action) => {
        state.uploadLoading = false;
        state.uploadResult = action.payload;
      })
      .addCase(uploadShopifyStock.rejected, (state, action) => {
        state.uploadLoading = false;
        state.uploadError = action.payload as string;
      })

      // Upload manual stock
      .addCase(uploadManualStock.pending, (state) => {
        state.uploadLoading = true;
        state.uploadError = null;
        state.uploadResult = null;
      })
      .addCase(uploadManualStock.fulfilled, (state, action) => {
        state.uploadLoading = false;
        state.uploadResult = action.payload;
      })
      .addCase(uploadManualStock.rejected, (state, action) => {
        state.uploadLoading = false;
        state.uploadError = action.payload as string;
      })

      // Upload bin allocation
      .addCase(uploadBinAllocation.pending, (state) => {
        state.uploadLoading = true;
        state.uploadError = null;
        state.uploadResult = null;
      })
      .addCase(uploadBinAllocation.fulfilled, (state, action) => {
        state.uploadLoading = false;
        state.uploadResult = action.payload;
      })
      .addCase(uploadBinAllocation.rejected, (state, action) => {
        state.uploadLoading = false;
        state.uploadError = action.payload as string;
      })

      // Fetch inventory status
      .addCase(fetchInventoryStatus.pending, (state) => {
        state.reportsLoading = true;
        state.reportsError = null;
      })
      .addCase(fetchInventoryStatus.fulfilled, (state, action) => {
        state.reportsLoading = false;
        state.inventoryStatus = action.payload.data;
      })
      .addCase(fetchInventoryStatus.rejected, (state, action) => {
        state.reportsLoading = false;
        state.reportsError = action.payload as string;
      })

      // Submit inventory adjustment
      .addCase(submitInventoryAdjustment.pending, (state) => {
        state.adjustmentLoading = true;
        state.adjustmentError = null;
        state.adjustmentSuccess = false;
      })
      .addCase(submitInventoryAdjustment.fulfilled, (state) => {
        state.adjustmentLoading = false;
        state.adjustmentSuccess = true;
      })
      .addCase(submitInventoryAdjustment.rejected, (state, action) => {
        state.adjustmentLoading = false;
        state.adjustmentError = action.payload as string;
        state.adjustmentSuccess = false;
      })

      // Fetch recent adjustments
      .addCase(fetchRecentAdjustments.pending, (state) => {
        state.adjustmentsLoading = true;
        state.adjustmentError = null;
      })
      .addCase(fetchRecentAdjustments.fulfilled, (state, action) => {
        state.adjustmentsLoading = false;
        state.recentAdjustments = action.payload.data || [];
      })
      .addCase(fetchRecentAdjustments.rejected, (state, action) => {
        state.adjustmentsLoading = false;
        state.adjustmentError = action.payload as string;
      })

      // Fetch adjustment summary
      .addCase(fetchAdjustmentSummary.pending, (state) => {
        state.adjustmentsLoading = true;
        state.adjustmentError = null;
      })
      .addCase(fetchAdjustmentSummary.fulfilled, (state, action) => {
        state.adjustmentsLoading = false;
        state.adjustmentSummary = action.payload.data;
      })
      .addCase(fetchAdjustmentSummary.rejected, (state, action) => {
        state.adjustmentsLoading = false;
        state.adjustmentError = action.payload as string;
      });
  }
});

export const {
  setStyleFilters,
  setInventoryFilters,
  setSelectedStyle,
  clearSearchResults,
  clearLocationSuggestions,
  clearUploadResult,
  clearError,
  clearAdjustmentMessages,
  resetState
} = inventorySlice.actions;

export default inventorySlice.reducer;
